import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { subscribeTable } from '@/lib/realtime';
import type { HottrConversationRow, HottrMessageRow } from '@/lib/database.types';
import {
  summarizeConversation,
  type ConversationSummary,
  type OtherProfileStub,
} from './conversationSummary';

export type { ConversationSummary } from './conversationSummary';

export interface ConversationsState {
  conversations: ConversationSummary[];
  loading: boolean;
}

export const useConversations = (): ConversationsState => {
  const { user } = useAuth();
  const [state, setState] = useState<ConversationsState>({
    conversations: [],
    loading: true,
  });

  const load = useCallback(async () => {
    if (!user) { setState({ conversations: [], loading: false }); return; }
    const supabase = getSupabase();
    if (!supabase) { setState({ conversations: [], loading: false }); return; }

    const { data: convs, error } = await supabase
      .from('hottr_conversations')
      .select('*')
      .or(`participant_a.eq.${user.id},participant_b.eq.${user.id}`)
      .order('last_message_at', { ascending: false, nullsFirst: false });

    if (error || !convs || convs.length === 0) {
      setState({ conversations: [], loading: false });
      return;
    }

    const rows = convs as HottrConversationRow[];
    const otherIds = rows.map((c) =>
      c.participant_a === user.id ? c.participant_b : c.participant_a,
    );
    const convIds = rows.map((c) => c.id);

    const { data: profilesRaw } = await supabase
      .from('app_profiles')
      .select('owner_id, handle, photo_url')
      .in('owner_id', otherIds);

    const profiles = (profilesRaw ?? []) as OtherProfileStub[];

    const { data: msgsRaw } = await supabase
      .from('hottr_messages')
      .select('*')
      .in('conversation_id', convIds)
      .order('created_at', { ascending: false });

    const msgs = (msgsRaw ?? []) as HottrMessageRow[];
    const userId = user.id;

    const summaries: ConversationSummary[] = rows.map((c) => {
      const otherId = c.participant_a === userId ? c.participant_b : c.participant_a;
      const profile = profiles.find((p) => p.owner_id === otherId) ?? null;
      const convMsgs = msgs.filter((m) => m.conversation_id === c.id);
      return summarizeConversation(c, convMsgs, profile, userId);
    });

    setState({ conversations: summaries, loading: false });
  }, [user?.id]);

  useEffect(() => {
    load();
    const supabase = getSupabase();
    if (!supabase || !user) return;

    return subscribeTable(
      supabase,
      'hottr-conversations-watch',
      [
        { event: '*', table: 'hottr_conversations' },
        { event: 'INSERT', table: 'hottr_messages' },
      ],
      load,
    );
  }, [load, user?.id]);

  return state;
};
